/**
 * Integration Manager - Complete UI for Integration governance type
 * 
 * Manages system connections and data mappings
 * Part of the 15 fundamental workflow types system
 */
// @ts-nocheck


import { useApolloClient } from '@apollo/client/react';
import { gql, useMutation, useQuery } from '@ferp/graphql-client';
import {
    ActionIcon,
    Alert,
    Badge,
    Button,
    Card,
    Code,
    Group,
    JsonInput,
    Menu,
    Modal,
    NumberInput,
    ScrollArea,
    Select,
    SimpleGrid,
    Stack,
    Table,
    Tabs,
    Text,
    TextInput,
    ThemeIcon,
    Title
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import {
    IconAlertTriangle,
    IconArrowsLeftRight,
    IconCheck,
    IconCopy,
    IconDots,
    IconLink,
    IconMapPin,
    IconPencil,
    IconPlugConnected,
    IconPlugConnectedX,
    IconPlus,
    IconRefresh,
    IconSettings,
    IconSparkles,
    IconTemplate,
    IconTestPipe,
    IconTrash,
    IconWand
} from '@tabler/icons-react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    expertRoutingService,
    type ExpertAssignment,
    type ExpertRoutingCriteria,
    type ProjectComplexity
} from '../services/ExpertRoutingService';
import {
    BusinessIntegrationService,
    type BusinessIntegrationTemplate
} from '../templates/BusinessIntegrationTemplates';
import type { ActorDescriptor, FieldMapping, Integration } from '../types-16-complete';
import { createExpertCommunicationSession } from './CommunicationManager';
import { useGenerateId } from '../hooks/useGenerateId';
import { EntityType } from '../types/EntityType';

interface IntegrationManagerProps {
  tenant: string;
  onIntegrationChange?: (integrations: Integration[]) => void;
  readonly?: boolean;
}

interface IntegrationStatus {
  connected: boolean;
  last_sync: Date;
  error_count: number;
  success_rate: number;
  avg_latency_ms: number;
}

export const IntegrationManager: React.FC<IntegrationManagerProps> = ({
  tenant,
  onIntegrationChange,
  readonly = false,
}) => {
  const [integrations, setIntegrations] = useState<Integration[]>([]);
  const [selectedIntegration, setSelectedIntegration] = useState<Integration | null>(null);
  const [editingIntegration, setEditingIntegration] = useState<Partial<Integration> | null>(null);
  const [loading, setLoading] = useState(false);
  const [statusData, setStatusData] = useState<Record<string, IntegrationStatus>>({});
  const [modalOpened, { open: openModal, close: closeModal }] = useDisclosure(false);
  const [templateModalOpened, { open: openTemplateModal, close: closeTemplateModal }] = useDisclosure(false);
  const [formErrors, setFormErrors] = useState<Record<string, string | undefined>>({});

  // FERP 5-layer enforcement: Backend ID generation only
  const { generateId } = useGenerateId();

  // Template-related state
  const [selectedTemplate, setSelectedTemplate] = useState<BusinessIntegrationTemplate | null>(null);
  const [templateParameters, setTemplateParameters] = useState<Record<string, any>>({});
  const [templateErrors, setTemplateErrors] = useState<Record<string, string>>({});
  const [templateCategories, setTemplateCategories] = useState<Record<string, any>>({});
  const [availableTemplates, setAvailableTemplates] = useState<BusinessIntegrationTemplate[]>([]);

  // Expert selection state
  const [expertAssignment, setExpertAssignment] = useState<ExpertAssignment | null>(null);
  const [selectedExpert, setSelectedExpert] = useState<ActorDescriptor | null>(null);
  const [showExpertSelection, setShowExpertSelection] = useState(false);
  const [expertSelectionLoading, setExpertSelectionLoading] = useState(false);

  // Initialize services
  const client = useApolloClient();
  const businessIntegrationService = useMemo(() => new BusinessIntegrationService(client), [client]);

  // GraphQL wiring
  const GET_INTEGRATIONS = gql`
    query GetIntegrations($tenant: String!) {
      integrations(tenant: $tenant) {
        id
        name
        provider
        connectionConfig
        mappings { source target transform }
        retryPolicy { maxRetries backoffMs timeoutMs }
        metadata
      }
    }
  `;
  const UPSERT_INTEGRATION = gql`
    mutation UpsertIntegration($tenant: String!, $integration: IntegrationInput!) {
      upsertIntegration(tenant: $tenant, integration: $integration) { id }
    }
  `;
  const DELETE_INTEGRATION = gql`
    mutation DeleteIntegration($tenant: String!, $id: ID!) { deleteIntegration(tenant: $tenant, id: $id) }
  `;
  const TEST_INTEGRATION = gql`
    mutation TestIntegration($tenant: String!, $id: ID!) {
      testIntegration(tenant: $tenant, id: $id) {
        connected
        error_count
        success_rate
        avg_latency_ms
        last_sync
      }
    }
  `;
  const SYNC_INTEGRATION = gql`
    mutation SyncIntegration($tenant: String!, $id: ID!) {
      syncIntegration(tenant: $tenant, id: $id) { last_sync }
    }
  `;

  // GraphQL mutations for template instantiation - creates all 4 primitive types
  const CREATE_RESOURCE = gql`
    mutation CreateResource($tenant: String!, $resource: ResourceInput!) {
      createResource(tenant: $tenant, resource: $resource) {
        id
        name
        resourceType
        status
        endpoint
        limits
        metadata
      }
    }
  `;

  const CREATE_WORKFLOW_DEFINITION = gql`
    mutation CreateWorkflowDefinition($tenant: String!, $workflow: WorkflowDefinitionInput!) {
      createWorkflowDefinition(tenant: $tenant, workflow: $workflow) {
        id
        name
        version
        description
        states
        metadata
      }
    }
  `;

  const CREATE_POLICY = gql`
    mutation CreatePolicy($tenant: String!, $policy: PolicyInput!) {
      createPolicy(tenant: $tenant, policy: $policy) {
        id
        name
        type
        rules
        metadata
      }
    }
  `;

  const CREATE_INTEGRATION = gql`
    mutation CreateIntegration($tenant: String!, $integration: IntegrationInput!) {
      createIntegration(tenant: $tenant, integration: $integration) {
        id
        name
        provider
        connectionConfig
        mappings
        retryPolicy
        metadata
      }
    }
  `;

  const { data, refetch } = useQuery<Record<string, any>>(GET_INTEGRATIONS, { variables: { tenant } } as any);
  const [mutateUpsert] = useMutation<Record<string, any>>(UPSERT_INTEGRATION);
  const [mutateDelete] = useMutation<Record<string, any>>(DELETE_INTEGRATION);
  const [mutateTest] = useMutation<Record<string, any>>(TEST_INTEGRATION);
  const [mutateSync] = useMutation<Record<string, any>>(SYNC_INTEGRATION);

  // Template instantiation mutations
  const [mutateCreateResource] = useMutation<Record<string, any>>(CREATE_RESOURCE);
  const [mutateCreateWorkflow] = useMutation<Record<string, any>>(CREATE_WORKFLOW_DEFINITION);
  const [mutateCreatePolicy] = useMutation<Record<string, any>>(CREATE_POLICY);
  const [mutateCreateIntegration] = useMutation<Record<string, any>>(CREATE_INTEGRATION);

  useEffect(() => {
    if (data?.integrations) {
      setIntegrations(data?.integrations as any);
    }
    // Status data should come from backend APIs
    // TODO: Implement integration status API
  }, [data]);

  // Load template data from APIs
  useEffect(() => {
    const loadTemplateData = async () => {
      try {
        const [categories, templates] = await Promise.all([
          businessIntegrationService.getTemplateCategories(tenant),
          businessIntegrationService.getAvailableTemplates(tenant)
        ]);
        setTemplateCategories(categories);
        setAvailableTemplates(templates);
      } catch (error) {
        console.error('Failed to load template data:', error);
        notifications.show({
          title: 'Template Loading Failed',
          message: 'Could not load business integration templates',
          color: 'red',
        });
      }
    };

    loadTemplateData();
  }, [businessIntegrationService]);

  const createNewIntegration = useCallback(() => {
    const newIntegration: Partial<Integration> = {
      name: '',
      provider: '',
      connectionConfig: {},
      mappings: [],
      retryPolicy: {
        maxRetries: 3,
        backoffMs: 1000,
        timeoutMs: 30000,
      },
      metadata: {},
    };
    setEditingIntegration(newIntegration);
    setSelectedIntegration(null);
    openModal();
  }, [openModal]);

  const editIntegration = useCallback((integration: Integration) => {
    setEditingIntegration({ ...integration });
    setSelectedIntegration(integration);
    openModal();
  }, [openModal]);

  const saveIntegration = useCallback(async () => {
    if (!editingIntegration || !editingIntegration.name) return;
    
    setLoading(true);
    try {
      const errs: string[] = [];
      if (!editingIntegration.name) errs.push('Name is required');
      if (!editingIntegration.provider) errs.push('Provider is required');
      if (errs.length > 0) {
        setFormErrors({
          name: !editingIntegration.name ? 'Required' : undefined,
          provider: !editingIntegration.provider ? 'Required' : undefined,
        });
        notifications.show({ title: 'Validation error', message: errs.join('; '), color: 'red' });
        return;
      }

      // FERP architecture: Backend ID generation (5-layer enforcement)
      const integrationId = editingIntegration.id || await generateId(EntityType.correlation);

      const integrationToSave: Integration = {
        id: integrationId,
        name: editingIntegration.name,
        provider: editingIntegration.provider || '',
        connectionConfig: editingIntegration.connectionConfig || {},
        mappings: editingIntegration.mappings || [],
        retryPolicy: editingIntegration.retryPolicy || {
          maxRetries: 3,
          backoffMs: 1000,
          timeoutMs: 30000,
        },
        metadata: editingIntegration.metadata || {},
      };

      const updatedIntegrations = selectedIntegration
        ? integrations.map(i => i.id === selectedIntegration.id ? integrationToSave : i)
        : [...integrations, integrationToSave];

      await mutateUpsert({ variables: { tenant, integration: integrationToSave } });
      await refetch?.();
      setIntegrations(updatedIntegrations);
      onIntegrationChange?.(updatedIntegrations);
      
      notifications.show({
        title: 'Success',
        message: `Integration "${integrationToSave.name}" ${selectedIntegration ? 'updated' : 'created'} successfully`,
        color: 'green',
        icon: <IconCheck size={16} />,
      });
      
      closeModal();
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: 'Failed to save integration',
        color: 'red',
        icon: <IconAlertTriangle size={16} />,
      });
    } finally {
      setLoading(false);
    }
  }, [editingIntegration, selectedIntegration, integrations, onIntegrationChange, closeModal]);

  const deleteIntegration = useCallback(async (integrationId: string) => {
    setLoading(true);
    try {
      await mutateDelete({ variables: { tenant, id: integrationId } });
      await refetch?.();
      const updatedIntegrations = integrations.filter(i => i.id !== integrationId);
      setIntegrations(updatedIntegrations);
      const newStatusData = { ...statusData };
      delete newStatusData[integrationId];
      setStatusData(newStatusData);
      onIntegrationChange?.(updatedIntegrations);
      notifications.show({ title: 'Integration Deleted', message: 'Integration has been removed', color: 'orange' });
    } finally {
      setLoading(false);
    }
  }, [integrations, statusData, onIntegrationChange, mutateDelete, tenant, refetch]);

  const testIntegration = useCallback(async (integration: Integration) => {
    setLoading(true);
    try {
      const res = await mutateTest({ variables: { tenant, id: integration.id } });
      const st = res?.data?.testIntegration;
      if (st) {
        const newStatus: IntegrationStatus = {
          connected: !!st.connected,
          last_sync: new Date(st.last_sync || Date.now()),
          error_count: st.error_count ?? 0,
          success_rate: st.success_rate ?? 0,
          avg_latency_ms: st.avg_latency_ms ?? 0,
        };
        setStatusData(prev => ({ ...prev, [integration.id]: newStatus }));
        notifications.show({
          title: 'Connection Test Complete',
          message: newStatus.connected ? 'Connection successful' : 'Connection failed',
          color: newStatus.connected ? 'green' : 'red',
          icon: newStatus.connected ? <IconCheck size={16} /> : <IconAlertTriangle size={16} />,
        });
      }
    } finally {
      setLoading(false);
    }
  }, [mutateTest, tenant]);

  const syncIntegration = useCallback(async (integration: Integration) => {
    setLoading(true);
    try {
      const res = await mutateSync({ variables: { tenant, id: integration.id } });
      const lastSync = res?.data?.syncIntegration?.last_sync;
      const newStatus: IntegrationStatus = {
        ...statusData[integration.id],
        last_sync: lastSync ? new Date(lastSync) : new Date(),
        connected: true,
        error_count: 0,
        success_rate: statusData[integration.id]?.success_rate ?? 0,
        avg_latency_ms: statusData[integration.id]?.avg_latency_ms ?? 0,
      };
      setStatusData(prev => ({ ...prev, [integration.id]: newStatus }));
      notifications.show({ title: 'Sync Complete', message: `${integration.name} has been synchronized`, color: 'blue', icon: <IconRefresh size={16} /> });
    } finally {
      setLoading(false);
    }
  }, [statusData, mutateSync, tenant]);

  // Expert routing handler - determines appropriate expert tier and assignment
  const handleExpertRouting = useCallback(async () => {
    if (!selectedTemplate) return;

    setExpertSelectionLoading(true);
    try {
      // Estimate project complexity and value based on template parameters
      const projectValue = estimateProjectValue(selectedTemplate, templateParameters);
      const complexity = estimateProjectComplexity(selectedTemplate, templateParameters);
      const estimatedHours = estimateProjectHours(selectedTemplate, templateParameters);

      // Build routing criteria
      const criteria: ExpertRoutingCriteria = {
        projectValue,
        complexity: complexity as ProjectComplexity,
        projectComplexity: complexity as ProjectComplexity,
        customerTier: 'enterprise', // TODO: Get from tenant metadata
        internalCapacity: 75, // TODO: Get from ActorRepository.getTeamCapacityMetrics()
        specialistAvailability: 2, // TODO: Get real availability data
        riskProfile: getRiskProfile(selectedTemplate, templateParameters),
        complianceFrameworks: templateParameters.compliance_frameworks || ['SOX'],
        requiredSpecializations: ['business_integration', 'api_development'],
        estimatedHours,
        isEnterpriseAccount: true, // TODO: Get from tenant data
        isReferenceCustomer: false, // TODO: Get from tenant metadata
        requiresCustomDevelopment: selectedTemplate.complexity === 'Advanced'
      };

      // Get expert assignment recommendation
      const assignment = await expertRoutingService.getExpertAssignment(
        criteria,
        selectedTemplate.name.toLowerCase(),
        tenant
      );

      setExpertAssignment(assignment);
      setShowExpertSelection(true);

      // Auto-select the recommended expert if confidence is high
      if (assignment.confidence > 85 && assignment.assignedExpert) {
        setSelectedExpert(assignment.assignedExpert);
      }

    } catch (error) {
      console.error('Expert routing failed:', error);
      notifications.show({
        title: 'Expert Routing Error',
        message: 'Failed to determine expert assignment. Please try again.',
        color: 'red'
      });
    } finally {
      setExpertSelectionLoading(false);
    }
  }, [selectedTemplate, templateParameters]);

  // Helper functions for expert routing
  const estimateProjectValue = (template: BusinessIntegrationTemplate, params: Record<string, any>): number => {
    const baseValue = {
      'Simple': 5000,
      'Moderate': 15000,
      'Advanced': 35000
    }[template.complexity] || 10000;

    // Adjust based on parameters
    let multiplier = 1;
    if (params.compliance_frameworks?.length > 2) multiplier += 0.5;
    if (params.employee_departments?.length > 3) multiplier += 0.3;
    if (params.sync_frequency === 'real-time') multiplier += 0.4;

    return Math.round(baseValue * multiplier);
  };

  const estimateProjectComplexity = (template: BusinessIntegrationTemplate, params: Record<string, any>): number => {
    const baseComplexity = {
      'Simple': 3,
      'Moderate': 6,
      'Advanced': 9
    }[template.complexity] || 5;

    let adjustment = 0;
    if (params.compliance_frameworks?.length > 2) adjustment += 2;
    if (params.employee_departments?.length > 4) adjustment += 1;
    if (params.sync_frequency === 'real-time') adjustment += 1;

    return Math.min(Math.max(baseComplexity + adjustment, 1), 10) as any;
  };

  const estimateProjectHours = (template: BusinessIntegrationTemplate, params: Record<string, any>): number => {
    const baseHours = parseInt(template.estimatedSetupTime?.split(' ')[0] || '30') || 30;

    let multiplier = 1;
    if (params.compliance_frameworks?.length > 2) multiplier += 0.8;
    if (params.employee_departments?.length > 3) multiplier += 0.5;
    if (template.complexity === 'Advanced') multiplier += 1.5;

    return Math.round(baseHours * multiplier);
  };

  const getRiskProfile = (template: BusinessIntegrationTemplate, params: Record<string, any>): 'low' | 'medium' | 'high' | 'critical' => {
    if (params.compliance_frameworks?.includes('HIPAA')) return 'critical';
    if (params.compliance_frameworks?.includes('SOX')) return 'high';
    if (template.category === 'Healthcare Systems') return 'critical';
    if (template.category === 'Financial Processing') return 'high';
    if (template.complexity === 'Advanced') return 'medium';
    return 'low';
  };

  // Template instantiation handler
  const instantiateTemplate = useCallback(async () => {
    if (!selectedTemplate) return;

    setLoading(true);
    try {
      // Validate template parameters
      const errors: Record<string, string> = {};
      for (const param of selectedTemplate.parameters) {
        const value = templateParameters[param.name];
        if (param.required && (!value || value === '')) {
          errors[param.name] = `${param.displayName} is required`;
        }
      }

      if (Object.keys(errors).length > 0) {
        setTemplateErrors(errors);
        notifications.show({
          title: 'Validation Error',
          message: 'Please fix the form errors and try again',
          color: 'red'
        });
        return;
      }

      // If expert hasn't been selected yet, trigger expert selection flow
      if (!selectedExpert && !showExpertSelection) {
        await handleExpertRouting();
        return;
      }

      // If we're showing expert selection, user needs to choose an expert first
      if (showExpertSelection && !selectedExpert) {
        notifications.show({
          title: 'Expert Selection Required',
          message: 'Please select an expert to proceed with the integration',
          color: 'orange'
        });
        return;
      }

      // Instantiate template to create all 4 primitive types
      const primitives = await businessIntegrationService.instantiateTemplate(
        selectedTemplate.id,
        templateParameters,
        tenant,
        'current-user', // TODO: Get actual user ID
        'current-session' // TODO: Get actual session ID
      );

      // Create all 4 primitive types via GraphQL - LIFE-CRITICAL: All must succeed or transaction fails
      const createdPrimitives = {
        resource: null as any,
        integration: null as any,
        workflow: null as any,
        policy: null as any
      };

      try {
        // 1. Create Resource (infrastructure capacity)
        const resourceResult = await mutateCreateResource({
          variables: {
            tenant,
            resource: {
              name: primitives.resource.name,
              resourceType: primitives.resource.resourceType,
              status: primitives.resource.status,
              endpoint: primitives.resource.endpoint,
              limits: primitives.resource.limits,
              metadata: primitives.resource.metadata
            }
          }
        });
        createdPrimitives.resource = resourceResult.data?.createResource;

        // 2. Create Integration (business data flow)
        const integrationResult = await mutateCreateIntegration({
          variables: {
            tenant,
            integration: {
              name: primitives.integration.name,
              provider: primitives.integration.provider,
              connectionConfig: primitives.integration.connectionConfig,
              mappings: primitives.integration.mappings,
              retryPolicy: primitives.integration.retryPolicy,
              metadata: primitives.integration.metadata
            }
          }
        });
        createdPrimitives.integration = integrationResult.data?.createIntegration;

        // 3. Create WorkflowDefinition (orchestration)
        const workflowResult = await mutateCreateWorkflow({
          variables: {
            tenant,
            workflow: {
              name: primitives.workflow.name,
              version: primitives.workflow.version,
              description: primitives.workflow.description,
              states: primitives.workflow.states,
              metadata: primitives.workflow.metadata
            }
          }
        });
        createdPrimitives.workflow = workflowResult.data?.createWorkflowDefinition;

        // 4. Create Policy (governance rules)
        const policyResult = await mutateCreatePolicy({
          variables: {
            tenant,
            policy: {
              name: primitives.policy.name,
              type: primitives.policy.type,
              rules: primitives.policy.rules,
              metadata: primitives.policy.metadata
            }
          }
        });
        createdPrimitives.policy = policyResult.data?.createPolicy;

        // Validate all primitives were created successfully
        if (!createdPrimitives.resource || !createdPrimitives.integration ||
            !createdPrimitives.workflow || !createdPrimitives.policy) {
          throw new Error('Failed to create all required primitive types');
        }

        // Add the integration to our local state
        const newIntegrations = [...integrations, createdPrimitives.integration];
        setIntegrations(newIntegrations);
        onIntegrationChange?.(newIntegrations);

        // Refresh integrations list to ensure consistency
        await refetch();

        // FERP Pattern: Create workflow instance and communication session using proper primitives
        if (selectedExpert) {
          try {
            // Use ExpertRoutingService to assign expert with workflow instance creation
            const expertRoutingCriteria: ExpertRoutingCriteria = {
              projectValue: estimateProjectValue(selectedTemplate, templateParameters),
              complexity: estimateProjectComplexity(selectedTemplate, templateParameters) as ProjectComplexity,
              projectComplexity: estimateProjectComplexity(selectedTemplate, templateParameters) as ProjectComplexity,
              customerTier: 'enterprise',
              internalCapacity: 75,
              specialistAvailability: 2,
              riskProfile: getRiskProfile(selectedTemplate, templateParameters),
              complianceFrameworks: templateParameters.compliance_frameworks || ['SOX'],
              requiredSpecializations: ['business_integration', 'api_development'],
              estimatedHours: estimateProjectHours(selectedTemplate, templateParameters),
              isEnterpriseAccount: true,
              isReferenceCustomer: false,
              requiresCustomDevelopment: selectedTemplate.complexity === 'Advanced'
            };

            // Assign expert to integration with workflow instance creation
            const assignmentResult = await expertRoutingService.assignExpertToIntegration(
              expertRoutingCriteria,
              selectedExpert.metadata?.id,
              createdPrimitives.integration.id,
              tenant
            );

            // Create communication context and session task using FERP primitives
            const communicationSession = await createExpertCommunicationSession(
              selectedExpert,
              createdPrimitives.integration.id,
              tenant,
              'video_call',
              expertRoutingCriteria.estimatedHours * 60 // Convert hours to minutes
            );

            console.log('Expert assignment with workflow instance:', {
              assignment: assignmentResult.assignment,
              workflowInstance: assignmentResult.workflowInstance,
              communicationContext: communicationSession.context,
              sessionTask: communicationSession.sessionTask
            });

          } catch (expertAssignmentError) {
            // Non-blocking error - integration still succeeds even if expert assignment fails
            console.error('Expert assignment failed:', expertAssignmentError);
            notifications.show({
              title: 'Expert Assignment Warning',
              message: 'Integration created successfully, but expert assignment failed. Manual assignment may be required.',
              color: 'orange'
            });
          }
        }

        notifications.show({
          title: 'Template Applied Successfully',
          message: `${selectedTemplate.businessFriendlyName} has been configured with Resource (${createdPrimitives.resource.id}), Integration (${createdPrimitives.integration.id}), Workflow (${createdPrimitives.workflow.id}), and Policy (${createdPrimitives.policy.id}) components${selectedExpert ? ` and assigned to ${selectedExpert.name}` : ''}`,
          color: 'green',
          icon: <IconSparkles size={16} />
        });

        console.log('Created primitives via GraphQL:', createdPrimitives);
        if (selectedExpert) {
          console.log('Assigned expert:', selectedExpert);
        }

      } catch (primitiveError) {
        // LIFE-CRITICAL: If any primitive creation fails, we need to clean up
        console.error('Primitive creation failed, attempting cleanup:', primitiveError);

        // TODO: Implement cleanup logic for partially created primitives
        // This would require delete mutations for each primitive type

        throw new Error(`Failed to create primitive types: ${primitiveError instanceof Error ? primitiveError.message : 'Unknown error'}`);
      }

      closeTemplateModal();
      setSelectedTemplate(null);
      setTemplateParameters({});
      setTemplateErrors({});

      // Reset expert selection state
      setExpertAssignment(null);
      setSelectedExpert(null);
      setShowExpertSelection(false);

    } catch (error) {
      console.error('Template instantiation failed:', error);
      notifications.show({
        title: 'Template Failed',
        message: error instanceof Error ? error.message : 'Failed to apply template',
        color: 'red',
        icon: <IconAlertTriangle size={16} />
      });
    } finally {
      setLoading(false);
    }
  }, [selectedTemplate, templateParameters, tenant, integrations, onIntegrationChange, closeTemplateModal]);

  // Handle template selection
  const selectTemplate = useCallback((template: BusinessIntegrationTemplate) => {
    setSelectedTemplate(template);
    // Initialize parameters with defaults
    const defaultParams: Record<string, any> = {};
    template.parameters.forEach(param => {
      if (param.default !== undefined) {
        defaultParams[param.name] = param.default;
      }
    });
    setTemplateParameters(defaultParams);
    setTemplateErrors({});

    // Reset expert selection state
    setExpertAssignment(null);
    setSelectedExpert(null);
    setShowExpertSelection(false);

    openTemplateModal();
  }, [openTemplateModal]);

  const getConnectionStatusIcon = (connected: boolean) => {
    return connected ? <IconPlugConnected size={16} color="green" /> : <IconPlugConnectedX size={16} color="red" />;
  };

  const formatLastSync = (lastSync: Date) => {
    const now = new Date();
    const diffMs = now.getTime() - lastSync.getTime();
    const diffMinutes = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    
    if (diffMinutes < 60) return `${diffMinutes}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${diffDays}d ago`;
  };

  return (
    <Stack gap="md">
      <Group justify="apart">
        <div>
          <Title order={3}>
            <Group gap="xs">
              <IconLink size={24} />
              Integration Management
            </Group>
          </Title>
          <Text size="sm" color="dimmed">
            System connections and data mappings ({integrations.length} integrations)
          </Text>
        </div>
        {!readonly && (
          <Group>
            <Button
              leftSection={<IconTemplate size={16} />}
              onClick={openTemplateModal}
              variant="gradient"
              gradient={{ from: 'blue', to: 'cyan' }}
            >
              Use Template
            </Button>
            <Button
              leftSection={<IconPlus size={16} />}
              onClick={createNewIntegration}
              variant="filled"
            >
              New Integration
            </Button>
          </Group>
        )}
      </Group>

      {integrations.length === 0 ? (
        <Alert icon={<IconLink size={16} />} title="No Integrations Configured">
          <Text size="sm">
            No integrations have been configured yet. Click &quot;New Integration&quot; to connect your first external system.
          </Text>
        </Alert>
      ) : (
        <Stack gap="sm">
          {integrations.map((integration) => {
            const status = statusData[integration.id];
            
            return (
              <Card key={integration.id} shadow="sm" padding="md" withBorder>
                <Group justify="apart" ta="left">
                  <div style={{ flex: 1 }}>
                    <Group gap="xs" ta="center">
                      <ThemeIcon size="sm" variant="light" color="blue">
                        <IconArrowsLeftRight size={16} />
                      </ThemeIcon>
                      <Text fw={600}>{integration.name}</Text>
                      <Badge 
                        color="blue"
                        variant="outline"
                        size="sm"
                      >
                        {integration.provider}
                      </Badge>
                      {status && (
                        <Badge 
                          color={status.connected ? 'green' : 'red'}
                          variant="light"
                          size="sm"
                          leftSection={getConnectionStatusIcon(status.connected)}
                        >
                          {status.connected ? 'Connected' : 'Disconnected'}
                        </Badge>
                      )}
                    </Group>
                    
                    <Text size="sm" color="dimmed" mt="xs">
                      {integration.mappings.length} field mapping{integration.mappings.length !== 1 ? 's' : ''}
                      {integration.metadata?.sync_frequency && ` • ${integration.metadata.sync_frequency} sync`}
                    </Text>
                    
                    {status && (
                      <Group gap="lg" mt="xs">
                        <Group gap="xs">
                          <Text size="xs" color="dimmed">Last sync:</Text>
                          <Text size="xs" fw={500}>
                            {formatLastSync(status.last_sync)}
                          </Text>
                        </Group>
                        <Group gap="xs">
                          <Text size="xs" color="dimmed">Success rate:</Text>
                          <Text size="xs" fw={500} color={status.success_rate > 95 ? 'green' : status.success_rate > 90 ? 'orange' : 'red'}>
                            {status.success_rate.toFixed(1)}%
                          </Text>
                        </Group>
                        <Group gap="xs">
                          <Text size="xs" color="dimmed">Avg latency:</Text>
                          <Text size="xs" fw={500}>
                            {status.avg_latency_ms}ms
                          </Text>
                        </Group>
                        {status.error_count > 0 && (
                          <Group gap="xs">
                            <Text size="xs" color="red">Errors:</Text>
                            <Text size="xs" fw={500} color="red">
                              {status.error_count}
                            </Text>
                          </Group>
                        )}
                      </Group>
                    )}
                    
                    {integration.metadata?.compliance && (
                      <Group gap="xs" mt="xs">
                        {integration.metadata.compliance.map((comp: string) => (
                          <Badge key={comp} color="purple" variant="outline" size="xs">
                            {comp}
                          </Badge>
                        ))}
                      </Group>
                    )}
                  </div>
                  
                  {!readonly && (
                    <Menu shadow="md" width={200}>
                      <Menu.Target>
                        <ActionIcon variant="subtle">
                          <IconDots size={16} />
                        </ActionIcon>
                      </Menu.Target>
                      
                      <Menu.Dropdown>
                        <Menu.Item 
                          leftSection={<IconPencil size={14} />}
                          onClick={() => editIntegration(integration)}
                        >
                          Edit Integration
                        </Menu.Item>
                        <Menu.Item 
                          leftSection={<IconTestPipe size={14} />}
                          onClick={() => testIntegration(integration)}
                          disabled={loading}
                        >
                          Test Connection
                        </Menu.Item>
                        <Menu.Item 
                          leftSection={<IconRefresh size={14} />}
                          onClick={() => syncIntegration(integration)}
                          disabled={loading}
                        >
                          Sync Now
                        </Menu.Item>
                        <Menu.Item
                          leftSection={<IconCopy size={14} />}
                          onClick={async () => {
                            // FERP architecture: Backend ID generation (5-layer enforcement)
                            const duplicateId = await generateId(EntityType.correlation);
                            const duplicate = {
                              ...integration,
                              id: duplicateId,
                              name: `${integration.name} (Copy)`
                            };
                            setIntegrations([...integrations, duplicate]);
                          }}
                        >
                          Duplicate
                        </Menu.Item>
                        <Menu.Divider />
                        <Menu.Item 
                          leftSection={<IconTrash size={14} />}
                          color="red"
                          onClick={() => deleteIntegration(integration.id)}
                        >
                          Delete
                        </Menu.Item>
                      </Menu.Dropdown>
                    </Menu>
                  )}
                </Group>
              </Card>
            );
          })}
        </Stack>
      )}

      <Modal
        opened={modalOpened}
        onClose={closeModal}
        title={selectedIntegration ? 'Edit Integration' : 'Create New Integration'}
        size="xl"
        overlayProps={{ opacity: 0.55, blur: 3 }}
      >
        {editingIntegration && (
          <Tabs defaultValue="basic" variant="outline">
            <Tabs.List>
              <Tabs.Tab value="basic" leftSection={<IconSettings size={16} />}>
                Basic Info
              </Tabs.Tab>
              <Tabs.Tab value="mappings" leftSection={<IconMapPin size={16} />}>
                Field Mappings ({editingIntegration.mappings?.length || 0})
              </Tabs.Tab>
              <Tabs.Tab value="connection" leftSection={<IconPlugConnected size={16} />}>
                Connection Config
              </Tabs.Tab>
              <Tabs.Tab value="retry" leftSection={<IconRefresh size={16} />}>
                Retry Policy
              </Tabs.Tab>
            </Tabs.List>

            <Tabs.Panel value="basic" pt="md">
              <Stack gap="md">
                <TextInput
                  label="Integration Name"
                  placeholder="Enter integration name"
                  required
                  value={editingIntegration.name || ''}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEditingIntegration({ ...editingIntegration, name: e.target.value })}
                  error={formErrors['name']}
                />
                
                <TextInput
                  label="Provider"
                  placeholder="Provider name (e.g., Salesforce, Epic, Stripe)"
                  required
                  value={editingIntegration.provider || ''}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEditingIntegration({ ...editingIntegration, provider: e.target.value })}
                  error={formErrors['provider']}
                />
                
                <JsonInput
                  label="Metadata"
                  placeholder="Enter metadata JSON"
                  value={JSON.stringify(editingIntegration.metadata || {}, null, 2)}
                  onChange={(value) => {
                    try {
                      setEditingIntegration({ ...editingIntegration, metadata: JSON.parse(value) });
                    } catch (e) {
                      // Invalid JSON, ignore
                    }
                  }}
                  minRows={4}
                />
              </Stack>
            </Tabs.Panel>

            <Tabs.Panel value="mappings" pt="md">
              <ScrollArea h={400}>
                <Stack gap="sm">
                  {editingIntegration.mappings && editingIntegration.mappings.length > 0 && (
                    <Table>
                      <thead>
                        <tr>
                          <th>Source Field</th>
                          <th>Target Field</th>
                          <th>Transform</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {editingIntegration.mappings.map((mapping, index) => (
                          <tr key={index}>
                            <td>
                              <Code style={{ fontFamily: 'monospace' }}>
                                {mapping.source}
                              </Code>
                            </td>
                            <td>
                              <Code>
                                {mapping.target}
                              </Code>
                            </td>
                            <td>
                              <Text size="xs" color="dimmed">
                                {mapping.transform || 'none'}
                              </Text>
                            </td>
                            <td>
                              <ActionIcon
                                color="red"
                                variant="subtle"
                                size="sm"
                                onClick={() => {
                                  const newMappings = editingIntegration.mappings?.filter((_: any, i) => i !== index) || [];
                                  setEditingIntegration({ ...editingIntegration, mappings: newMappings });
                                }}
                              >
                                <IconTrash size={14} />
                              </ActionIcon>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  )}
                  
                  <Card withBorder padding="sm">
                    <Text size="sm" fw={500} mb="sm">Add New Mapping</Text>
                    <Stack gap="xs">
                      <TextInput
                        placeholder="Source field path (e.g., patient.name.given[0])"
                        size="xs"
                        id="new-mapping-source"
                      />
                      <TextInput
                        placeholder="Target field name (e.g., firstName)"
                        size="xs"
                        id="new-mapping-target"
                      />
                      <TextInput
                        placeholder="Transform function (optional)"
                        size="xs"
                        id="new-mapping-transform"
                      />
                      <Button
                        size="xs"
                        variant="light"
                        leftSection={<IconPlus size={14} />}
                        onClick={() => {
                          const sourceEl = document.getElementById('new-mapping-source') as HTMLInputElement;
                          const targetEl = document.getElementById('new-mapping-target') as HTMLInputElement;
                          const transformEl = document.getElementById('new-mapping-transform') as HTMLInputElement;
                          
                          if (sourceEl?.value && targetEl?.value) {
                            const newMapping: FieldMapping = {
                              source: sourceEl.value,
                              target: targetEl.value,
                              transform: transformEl?.value || undefined,
                            };
                            
                            setEditingIntegration({
                              ...editingIntegration,
                              mappings: [...(editingIntegration.mappings || []), newMapping],
                            });
                            
                            sourceEl.value = '';
                            targetEl.value = '';
                            transformEl.value = '';
                          }
                        }}
                      >
                        Add Mapping
                      </Button>
                    </Stack>
                  </Card>
                </Stack>
              </ScrollArea>
            </Tabs.Panel>

            <Tabs.Panel value="connection" pt="md">
              <Stack gap="md">
                <Alert icon={<IconPlugConnected size={16} />} color="blue">
                  Configure connection parameters, authentication, and endpoints.
                </Alert>
                
                <JsonInput
                  label="Connection Configuration"
                  placeholder={`{
  "base_url": "https://api.example.com",
  "auth_type": "oauth2",
  "client_id": "your-client-id",
  "timeoutMs": 30000
}`}
                  value={JSON.stringify(editingIntegration.connectionConfig || {}, null, 2)}
                  onChange={(value) => {
                    try {
                      setEditingIntegration({ ...editingIntegration, connectionConfig: JSON.parse(value) });
                    } catch (e) {
                      // Invalid JSON, ignore
                    }
                  }}
                  minRows={8}
                />
              </Stack>
            </Tabs.Panel>

            <Tabs.Panel value="retry" pt="md">
              <Stack gap="md">
                <Group grow>
                  <NumberInput
                    label="Max Retries"
                    placeholder="3"
                    value={editingIntegration.retryPolicy?.maxRetries}
                    onChange={(value: number | string | null) => setEditingIntegration({
                      ...editingIntegration,
                      retryPolicy: {
                        ...editingIntegration.retryPolicy!,
                        maxRetries: typeof value === 'number' ? value : 0
                      }
                    })}
                    min={0}
                    max={10}
                  />
                  
                  <NumberInput
                    label="Backoff Time (ms)"
                    placeholder="1000"
                    value={editingIntegration.retryPolicy?.backoffMs}
                    onChange={(value: number | string | null) => setEditingIntegration({
                      ...editingIntegration,
                      retryPolicy: {
                        ...editingIntegration.retryPolicy!,
                        backoffMs: typeof value === 'number' ? value : 1000
                      }
                    })}
                    min={100}
                    max={30000}
                    step={100}
                  />
                  
                  <NumberInput
                    label="Timeout (ms)"
                    placeholder="30000"
                    value={editingIntegration.retryPolicy?.timeoutMs}
                    onChange={(value: number | string | null) => setEditingIntegration({
                      ...editingIntegration,
                      retryPolicy: {
                        ...editingIntegration.retryPolicy!,
                        timeoutMs: typeof value === 'number' ? value : 30000
                      }
                    })}
                    min={1000}
                    max={300000}
                    step={1000}
                  />
                </Group>
                
                <Alert icon={<IconRefresh size={16} />} color="yellow">
                  <Text size="sm">
                    Retry policy determines how failed requests are handled. Exponential backoff is recommended for most integrations.
                  </Text>
                </Alert>
              </Stack>
            </Tabs.Panel>
          </Tabs>
        )}
        
        <Group justify="apart" mt="md">
          <Button variant="light" onClick={closeModal}>
            Cancel
          </Button>
          
          <Button
            onClick={saveIntegration}
            loading={loading}
            leftSection={<IconCheck size={16} />}
          >
            {selectedIntegration ? 'Update' : 'Create'} Integration
          </Button>
        </Group>
      </Modal>

      {/* Business Template Selection Modal */}
      <Modal
        opened={templateModalOpened}
        onClose={closeTemplateModal}
        title={selectedTemplate ? `Configure ${selectedTemplate.businessFriendlyName}` : 'Choose Integration Template'}
        size="xl"
        overlayProps={{ opacity: 0.55, blur: 3 }}
      >
        {!selectedTemplate ? (
          // Template selection view
          <Stack gap="md">
            <Alert icon={<IconSparkles size={16} />} color="blue">
              <Text size="sm">
                Templates provide pre-configured integrations that create Resource, Integration, Workflow, and Policy components automatically.
                Choose a template that matches your business needs.
              </Text>
            </Alert>

            {Object.entries(templateCategories).map(([categoryName, category]) => (
              <div key={categoryName}>
                <Group gap="xs" mb="sm">
                  <Text size="lg">{category.icon}</Text>
                  <Text size="md" fw={600}>{categoryName}</Text>
                </Group>
                <Text size="sm" color="dimmed" mb="md">
                  {category.description}
                </Text>

                <SimpleGrid cols={{ base: 2 }} spacing="md" mb="xl">
                  {category.templates.map((template: BusinessIntegrationTemplate) => (
                    <Card
                      key={template.id}
                      shadow="sm"
                      padding="md"
                      withBorder
                      style={{ cursor: 'pointer' }}
                      onClick={() => selectTemplate(template)}
                    >
                      <Group gap="xs" ta="left" mb="sm">
                        <Text size="xl">{template.icon}</Text>
                        <div style={{ flex: 1 }}>
                          <Text size="md" fw={600}>
                            {template.businessFriendlyName}
                          </Text>
                          <Text size="sm" color="dimmed">
                            {template.description}
                          </Text>
                        </div>
                        <Badge color="blue" variant="outline" size="sm">
                          {template.complexity}
                        </Badge>
                      </Group>

                      <Group gap="xs" mb="sm">
                        <Badge color="green" variant="light" size="xs">
                          {template.businessValue.timeSavings}
                        </Badge>
                        <Badge color="purple" variant="light" size="xs">
                          {template.estimatedSetupTime}
                        </Badge>
                      </Group>

                      <Text size="xs" color="dimmed">
                        Creates: Resource + Integration + Workflow + Policy
                      </Text>
                    </Card>
                  ))}
                </SimpleGrid>
              </div>
            ))}
          </Stack>
        ) : (
          // Template configuration view
          <Stack gap="md">
            <Alert icon={<IconWand size={16} />} color="blue">
              <Text size="sm">
                <strong>{selectedTemplate.businessFriendlyName}</strong> will create:
              </Text>
              <Text size="sm" mt="xs">
                • <strong>Resource</strong>: Infrastructure capacity and health monitoring<br/>
                • <strong>Integration</strong>: Business data flow and field mappings<br/>
                • <strong>Workflow</strong>: Orchestration and sync processes<br/>
                • <strong>Policy</strong>: Compliance and governance rules
              </Text>
            </Alert>

            <Text size="lg" fw={600} mb="md">
              Configuration Parameters
            </Text>

            <ScrollArea h={400}>
              <Stack gap="md">
                {selectedTemplate.parameters.map((param) => (
                  <div key={param.name}>
                    {param.type === 'string' || param.type === 'url' ? (
                      <TextInput
                        label={param.displayName}
                        description={param.description}
                        placeholder={param.default?.toString()}
                        required={param.required}
                        value={templateParameters[param.name] || ''}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          setTemplateParameters(prev => ({ ...prev, [param.name]: e.target.value }))
                        }
                        error={templateErrors[param.name]}
                      />
                    ) : param.type === 'select' ? (
                      <Select
                        label={param.displayName}
                        description={param.description}
                        data={param.options || []}
                        value={templateParameters[param.name] || param.default}
                        onChange={(value: string | null) =>
                          setTemplateParameters(prev => ({ ...prev, [param.name]: value }))
                        }
                        error={templateErrors[param.name]}
                      />
                    ) : param.type === 'multi-select' ? (
                      <Select
                        label={param.displayName}
                        description={param.description}
                        data={param.options || []}
                        multiple
                        value={templateParameters[param.name] || param.default || []}
                        onChange={(value: string[]) =>
                          setTemplateParameters(prev => ({ ...prev, [param.name]: value }))
                        }
                        error={templateErrors[param.name]}
                      />
                    ) : param.type === 'number' ? (
                      <NumberInput
                        label={param.displayName}
                        description={param.description}
                        value={templateParameters[param.name] || param.default}
                        onChange={(value: number | string | null) =>
                          setTemplateParameters(prev => ({ ...prev, [param.name]: value }))
                        }
                        error={templateErrors[param.name]}
                      />
                    ) : null}
                  </div>
                ))}
              </Stack>
            </ScrollArea>

            <Alert icon={<IconAlertTriangle size={16} />} color="yellow">
              <Text size="sm">
                <strong>Business Value:</strong><br/>
                • {selectedTemplate.businessValue.timeSavings}<br/>
                • {selectedTemplate.businessValue.costReduction}<br/>
                • {selectedTemplate.businessValue.complianceAutomation}
              </Text>
            </Alert>

            {/* Expert Selection Section */}
            {showExpertSelection && expertAssignment && (
              <Alert icon={<IconSparkles size={16} />} color="blue" mt="md">
                <Stack gap="sm">
                  <Text size="md" fw={600}>Expert Assignment Recommendation</Text>

                  <Text size="sm">
                    <strong>Recommended:</strong> {expertAssignment.assignedExpert?.name || 'AI Assistant'}
                    ({expertAssignment.recommendedTier.toUpperCase()})
                  </Text>

                  <Text size="sm">
                    <strong>Estimated Cost:</strong> ${expertAssignment.estimatedCost.toLocaleString()}
                    ({expertAssignment.estimatedDuration} hours)
                  </Text>

                  <Text size="sm">
                    <strong>Rationale:</strong> {expertAssignment.rationale}
                  </Text>

                  <Text size="sm" color="dimmed">
                    Confidence: {expertAssignment.confidence}%
                  </Text>

                  {/* Expert Options */}
                  <Text size="sm" fw={600} mt="sm">Available Experts:</Text>
                  <SimpleGrid cols={{ base: 1 }} spacing="xs">
                    {/* Recommended Expert */}
                    {expertAssignment.assignedExpert && (
                      <Card
                        padding="sm"
                        withBorder
                        style={{
                          cursor: 'pointer',
                          backgroundColor: selectedExpert?.metadata?.id === expertAssignment.assignedExpert.metadata?.id ? '#e7f3ff' : undefined
                        }}
                        onClick={() => setSelectedExpert(expertAssignment.assignedExpert!)}
                      >
                        <Group justify="apart" ta="center">
                          <div>
                            <Text size="sm" fw={600}>
                              {expertAssignment.assignedExpert.name} (Recommended)
                            </Text>
                            <Text size="xs" color="dimmed">
                              {expertAssignment.assignedExpert.metadata?.specializations?.slice(0, 3)?.join(', ') || 'No specializations'}
                            </Text>
                            <Text size="xs" color="green">
                              {expertAssignment.assignedExpert.metadata?.successRate || 0}% success rate,
                              {expertAssignment.assignedExpert.metadata?.avgResponseTime || 0}h response time
                            </Text>
                          </div>
                          <div style={{ textAlign: 'right' }}>
                            <Badge color="blue" variant="light">
                              {expertAssignment.recommendedTier.toUpperCase()}
                            </Badge>
                            <Text size="xs" fw={600}>
                              ${expertAssignment.assignedExpert.metadata?.hourlyRate || 0}/hour
                            </Text>
                          </div>
                        </Group>
                      </Card>
                    )}

                    {/* Alternative Options */}
                    {expertAssignment.alternativeOptions.map((option, index) => (
                      <Card
                        key={index}
                        padding="sm"
                        withBorder
                        style={{
                          cursor: 'pointer',
                          backgroundColor: selectedExpert?.metadata?.id === option.expert?.metadata?.id ? '#e7f3ff' : undefined,
                          opacity: option.tier === expertAssignment.recommendedTier ? 0.7 : 1
                        }}
                        onClick={() => option.expert && setSelectedExpert(option.expert)}
                      >
                        <Group justify="apart" ta="center">
                          <div>
                            <Text size="sm" fw={600}>
                              {option.expert?.name || `${option.tier.toUpperCase()} Expert`}
                            </Text>
                            <Text size="xs" color="dimmed">
                              {option.description}
                            </Text>
                            <Text size="xs" color={option.availability < 3 ? 'green' : 'orange'}>
                              Available in {option.availability} day{option.availability !== 1 ? 's' : ''}
                            </Text>
                          </div>
                          <div style={{ textAlign: 'right' }}>
                            <Badge
                              color={option.tier === 'tier1' ? 'green' : option.tier === 'tier2' ? 'blue' : 'gray'}
                              variant="light"
                            >
                              {option.tier.toUpperCase()}
                            </Badge>
                            <Text size="xs" fw={600}>
                              ${Math.round(option.cost / expertAssignment.estimatedDuration)}/hour
                            </Text>
                            <Text size="xs" color="dimmed">
                              ${option.cost.toLocaleString()} total
                            </Text>
                          </div>
                        </Group>
                      </Card>
                    ))}
                  </SimpleGrid>

                  {selectedExpert && (
                    <Alert color="green" mt="sm">
                      <Text size="sm">
                        ✅ <strong>{selectedExpert.name}</strong> selected for this integration
                      </Text>
                    </Alert>
                  )}
                </Stack>
              </Alert>
            )}
          </Stack>
        )}

        <Group justify="apart" mt="md">
          <Button
            variant="light"
            onClick={() => {
              if (showExpertSelection) {
                // Go back to template configuration
                setShowExpertSelection(false);
                setExpertAssignment(null);
                setSelectedExpert(null);
              } else if (selectedTemplate) {
                // Go back to template selection
                setSelectedTemplate(null);
                setTemplateParameters({});
                setTemplateErrors({});
              } else {
                // Close modal
                closeTemplateModal();
              }
            }}
          >
            {showExpertSelection ? 'Back to Configuration' : selectedTemplate ? 'Back to Templates' : 'Cancel'}
          </Button>

          {selectedTemplate && (
            <Button
              onClick={instantiateTemplate}
              loading={loading || expertSelectionLoading}
              leftSection={<IconSparkles size={16} />}
              variant="gradient"
              gradient={{ from: 'blue', to: 'cyan' }}
              disabled={showExpertSelection && !selectedExpert}
            >
              {showExpertSelection ? 'Apply Template with Expert' : 'Continue to Expert Selection'}
            </Button>
          )}
        </Group>
      </Modal>
    </Stack>
  );
};

export default IntegrationManager;